import { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  Braces,
  Clock,
  Zap
} from "lucide-react";
import { FormField, TextInput, ErrorAlert } from "../modals/FormComponents";
import {
  getWorkflow,
  startWorkflowRun,
  getWorkflowRun
} from "../../services/workflowService";
import { useEndpoints } from "../../hooks/useEndpoints";
import GraphView from "./GraphView";

const TERMINAL = ["success", "failed", "error", "completed", "cancelled"];
const POLL_MS = 800; // tight enough to catch waves live, light on the DB
const MAX_POLLS = 80;
const WAVE_MIN_MS = 650; // a fast branch step still gets a visible beat
const WAVE_MAX_MS = 1600; // cap slow API calls so the replay stays snappy
const WAVE_GAP_MS = 220;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const lc = (s) => (s || "").toLowerCase();

const fmtKey = (k) =>
  k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const isScalar = (v) =>
  v === null || ["string", "number", "boolean"].includes(typeof v);
const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

/**
 * RunVisualizer — a full-screen "watch it run" experience. Collects inputs,
 * starts a run, polls to completion, then replays the execution wave-by-wave on
 * the canvas (nodes light up, edges flow) before revealing the result data.
 */
export default function RunVisualizer({ workflow, onClose }) {
  const { endpoints } = useEndpoints();

  const [definition, setDefinition] = useState(null);
  const [inputsSpec, setInputsSpec] = useState({});
  const [values, setValues] = useState({});
  const [loadingDef, setLoadingDef] = useState(true);
  const [defError, setDefError] = useState(null);

  const [phase, setPhase] = useState("form"); // form | running | playing | done
  const [runError, setRunError] = useState(null);
  const [finalRun, setFinalRun] = useState(null);
  const [liveSteps, setLiveSteps] = useState({}); // {step_name: status} driving the canvas
  const [selectedStep, setSelectedStep] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const mounted = useRef(false);
  const playToken = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      playToken.current += 1; // cancel any in-flight replay
    };
  }, []);

  // Load the workflow's definition (canvas) + declared inputs.
  useEffect(() => {
    (async () => {
      setLoadingDef(true);
      setDefError(null);
      const res = await getWorkflow(workflow.id);
      if (!mounted.current) return;
      if (res.success && res.data) {
        const defn = res.data.latest_definition || { steps: {}, inputs: {} };
        setDefinition(defn);
        const spec = defn.inputs || {};
        setInputsSpec(spec);
        const initial = {};
        Object.entries(spec).forEach(([name, d]) => {
          initial[name] = d?.default != null ? String(d.default) : "";
        });
        setValues(initial);
      } else {
        setDefError(res.message || "Failed to load workflow inputs");
      }
      setLoadingDef(false);
    })();
  }, [workflow.id]);

  // Replay the finished run wave-by-wave so the user sees it flow.
  const replay = useCallback(async (run) => {
    const steps = Array.isArray(run.steps) ? run.steps : [];
    if (steps.length === 0) {
      setPhase("done");
      return;
    }
    const byWave = {};
    steps.forEach((s) => {
      const w = s.wave_index ?? s.step_index ?? 0;
      (byWave[w] = byWave[w] || []).push(s);
    });
    const waves = Object.keys(byWave)
      .map(Number)
      .sort((a, b) => a - b)
      .map((w) => byWave[w]);

    const token = ++playToken.current;
    setPhase("playing");
    const live = {};
    for (const wave of waves) {
      // Light the wave up as "running" (skipped steps go straight to skipped).
      wave.forEach((s) => {
        live[s.step_name] = lc(s.status) === "skipped" ? "skipped" : "running";
      });
      setLiveSteps({ ...live });
      const anyRunning = wave.some((s) => lc(s.status) !== "skipped");
      if (anyRunning) {
        const maxMs = Math.max(0, ...wave.map((s) => s.duration_ms || 0));
        await sleep(Math.min(Math.max(maxMs, WAVE_MIN_MS), WAVE_MAX_MS));
        if (token !== playToken.current) return;
        // Settle to each step's real final status.
        wave.forEach((s) => {
          live[s.step_name] = lc(s.status);
        });
        setLiveSteps({ ...live });
      }
      await sleep(WAVE_GAP_MS);
      if (token !== playToken.current) return;
    }
    if (token !== playToken.current) return;
    setPhase("done");
  }, []);

  const stepsToMap = (steps) => {
    const m = {};
    (steps || []).forEach((s) => {
      m[s.step_name] = lc(s.status);
    });
    return m;
  };

  // Poll the run and reflect real per-step status LIVE as each wave commits —
  // the wait itself becomes the visualization. Only if the run finishes before
  // we ever catch it mid-flight do we fall back to a staged replay.
  const watch = useCallback(
    async (runId) => {
      const token = ++playToken.current;
      let tries = 0;
      let midFrames = 0;
      while (mounted.current && token === playToken.current && tries < MAX_POLLS) {
        const res = await getWorkflowRun(runId);
        if (!mounted.current || token !== playToken.current) return;
        if (!res.success) {
          setRunError(res.message || "Failed to read run status");
          setPhase("done");
          return;
        }
        const data = res.data;
        const steps = Array.isArray(data.steps) ? data.steps : [];
        const term = TERMINAL.includes(lc(data.status));
        if (!term && steps.length > 0) {
          setLiveSteps(stepsToMap(steps));
          setPhase("playing");
          midFrames += 1;
        }
        if (term) {
          setFinalRun(data);
          if (midFrames === 0) {
            await replay(data); // never caught it live → animate the reveal
          } else {
            setLiveSteps(stepsToMap(steps));
            setPhase("done");
          }
          return;
        }
        await sleep(POLL_MS);
        tries += 1;
      }
      if (mounted.current && token === playToken.current) setPhase("done");
    },
    [replay]
  );

  const handleRun = async (e) => {
    e.preventDefault();
    setRunError(null);
    setSelectedStep(null);
    setShowRaw(false);
    setLiveSteps({});
    setFinalRun(null);
    setPhase("running");
    const res = await startWorkflowRun(workflow.id, values);
    if (!mounted.current) return;
    if (!res.success || !res.data?.run_id) {
      setRunError(res.message || "Failed to start run");
      setPhase("done");
      return;
    }
    watch(res.data.run_id);
  };

  const reset = () => {
    playToken.current += 1;
    setFinalRun(null);
    setLiveSteps({});
    setRunError(null);
    setSelectedStep(null);
    setShowRaw(false);
    setPhase("form");
  };

  const inputEntries = Object.entries(inputsSpec);
  // While running/playing, drive the canvas from the live step map. Once done,
  // switch to the full run so per-step durations + result chips land on nodes.
  const canvasRun =
    phase === "done" && finalRun
      ? { steps: finalRun.steps, result: finalRun.result }
      : {
          steps: Object.entries(liveSteps).map(([step_name, status]) => ({
            step_name,
            status
          }))
        };
  const status = lc(finalRun?.status);
  const ok = status === "success" || status === "completed";
  const runningNames = Object.entries(liveSteps)
    .filter(([, s]) => s === "running")
    .map(([n]) => n);

  const selStepData =
    selectedStep && finalRun?.steps
      ? finalRun.steps.find((s) => s.step_name === selectedStep)
      : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full h-full max-w-7xl max-h-[94vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="shrink-0 px-6 py-3.5 border-b border-gray-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
            <Play className="w-4 h-4 fill-current" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-900 truncate">
              Run · {workflow.name}
            </h2>
            <RunStatusLine
              phase={phase}
              runningNames={runningNames}
              status={status}
              ok={ok}
            />
          </div>
          <button
            onClick={onClose}
            className="ml-auto p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* FORM */}
        {phase === "form" && (
          <div className="flex-1 overflow-auto flex items-start justify-center p-8">
            <form
              onSubmit={handleRun}
              className="w-full max-w-md bg-white rounded-xl border border-gray-200 p-6 space-y-4"
            >
              <ErrorAlert message={defError} />
              <p className="text-sm text-gray-500">
                Provide the inputs, then watch the workflow execute step by step.
              </p>
              {loadingDef ? (
                <div className="flex items-center gap-2 text-sm text-gray-600 py-6">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading inputs…
                </div>
              ) : inputEntries.length === 0 ? (
                <p className="text-sm text-gray-600">
                  This workflow has no inputs — just run it.
                </p>
              ) : (
                inputEntries.map(([name, d]) => (
                  <FormField key={name} label={name} required={!!d?.required}>
                    <TextInput
                      id={name}
                      name={name}
                      value={values[name] ?? ""}
                      onChange={(e) =>
                        setValues((p) => ({ ...p, [name]: e.target.value }))
                      }
                      placeholder={d?.description || d?.type || "value"}
                      required={!!d?.required}
                    />
                  </FormField>
                ))
              )}
              <div className="flex justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loadingDef}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Play className="w-4 h-4 fill-current" /> Run workflow
                </button>
              </div>
            </form>
          </div>
        )}

        {/* RUNNING / PLAYING / DONE — canvas + data */}
        {phase !== "form" && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Canvas */}
            <div className="flex-1 min-h-0 bg-[#FCFCFC] relative">
              {definition && Object.keys(definition.steps || {}).length > 0 ? (
                <GraphView
                  definition={definition}
                  endpoints={endpoints}
                  run={canvasRun}
                  workflowId={workflow.id}
                  selectedStep={selectedStep}
                  onSelectStep={(n) => phase === "done" && setSelectedStep(n)}
                  interactive={false}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-400">
                  No steps to visualize.
                </div>
              )}
              {phase === "running" && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px]">
                  <div className="flex items-center gap-2 bg-white shadow-lg rounded-full px-4 py-2 border border-gray-200">
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                    <span className="text-sm font-medium text-gray-700">
                      Starting run…
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Data / result strip */}
            <div className="shrink-0 border-t border-gray-200 bg-white max-h-[42%] overflow-auto">
              {runError && (
                <div className="p-4">
                  <ErrorAlert message={runError} />
                </div>
              )}

              {phase === "playing" && (
                <div className="p-5 flex items-center gap-3 text-sm text-gray-600">
                  <Zap className="w-4 h-4 text-blue-600" />
                  <span>
                    Executing
                    {runningNames.length > 0 && (
                      <>
                        {" "}
                        <span className="font-mono font-medium text-gray-900">
                          {runningNames.join(", ")}
                        </span>
                      </>
                    )}
                    …
                  </span>
                </div>
              )}

              {phase === "done" && finalRun && (
                <div className="p-5">
                  {selStepData ? (
                    <StepOutput
                      step={selStepData}
                      onBack={() => setSelectedStep(null)}
                    />
                  ) : (
                    <ResultPanel
                      run={finalRun}
                      ok={ok}
                      showRaw={showRaw}
                      onToggleRaw={() => setShowRaw((v) => !v)}
                    />
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      onClick={reset}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      Run again
                    </button>
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RunStatusLine({ phase, runningNames, status, ok }) {
  if (phase === "form")
    return <p className="text-xs text-gray-500">Configure inputs to run</p>;
  if (phase === "running")
    return (
      <p className="text-xs text-blue-600 flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" /> Starting…
      </p>
    );
  if (phase === "playing")
    return (
      <p className="text-xs text-blue-600 flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
        Running {runningNames.length ? runningNames.join(", ") : "…"}
      </p>
    );
  return (
    <p
      className={`text-xs flex items-center gap-1 ${
        ok ? "text-green-600" : "text-red-600"
      }`}
    >
      {ok ? (
        <CheckCircle2 className="w-3.5 h-3.5" />
      ) : (
        <XCircle className="w-3.5 h-3.5" />
      )}
      Run {status || "finished"}
    </p>
  );
}

function ResultPanel({ run, ok, showRaw, onToggleRaw }) {
  const result = run.result || {};
  const entries = Object.entries(result);
  const scalars = entries.filter(([, v]) => isScalar(v));
  const complex = entries.filter(([, v]) => !isScalar(v));

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {ok ? (
          <CheckCircle2 className="w-4 h-4 text-green-600" />
        ) : (
          <XCircle className="w-4 h-4 text-red-500" />
        )}
        <span className="text-sm font-semibold text-gray-900">Result</span>
        <span className="text-xs text-gray-400">· click any node for its step output</span>
        {entries.length > 0 && (
          <button
            onClick={onToggleRaw}
            className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${
              showRaw ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <Braces className="w-3.5 h-3.5" /> Raw
          </button>
        )}
      </div>

      {run.error_message && (
        <p className="text-xs text-red-600 break-words mb-3">{run.error_message}</p>
      )}

      {entries.length === 0 && !run.error_message && (
        <p className="text-sm text-gray-500">No result object returned.</p>
      )}

      {showRaw ? (
        <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-auto max-h-72">
          {JSON.stringify(result, null, 2)}
        </pre>
      ) : (
        <>
          {scalars.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {scalars.map(([k, v]) => (
                <div
                  key={k}
                  className="rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2"
                >
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 truncate">
                    {fmtKey(k)}
                  </div>
                  <div
                    className="text-sm font-medium text-gray-900 truncate"
                    title={fmtVal(v)}
                  >
                    {fmtVal(v)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {complex.map(([k, v]) => (
            <div key={k} className="mt-3">
              <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">
                {fmtKey(k)}
              </div>
              <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-auto max-h-44">
                {JSON.stringify(v, null, 2)}
              </pre>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function StepOutput({ step, onBack }) {
  const st = lc(step.status);
  const body =
    step.output && typeof step.output === "object"
      ? step.output.response_body ?? step.output
      : step.output;
  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 mb-2"
      >
        <ChevronLeft className="w-3.5 h-3.5" /> Back to result
      </button>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-mono text-sm font-semibold text-gray-900">
          {step.step_name}
        </span>
        <span
          className={`px-2 py-0.5 rounded text-[11px] font-medium ${
            st === "success"
              ? "bg-green-100 text-green-700"
              : st === "skipped"
              ? "bg-gray-100 text-gray-500"
              : "bg-red-100 text-red-700"
          }`}
        >
          {step.status}
        </span>
        {step.duration_ms != null && (
          <span className="flex items-center gap-1 text-[11px] text-gray-400">
            <Clock className="w-3 h-3" /> {step.duration_ms} ms
          </span>
        )}
      </div>
      {step.error_message && (
        <p className="text-xs text-red-600 break-words mb-2">{step.error_message}</p>
      )}
      <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-auto max-h-72">
        {body == null ? "No output." : JSON.stringify(body, null, 2)}
      </pre>
    </div>
  );
}
